import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Star, Quote, MessageCircle, ChevronLeft, ChevronRight, PenSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ReviewModal from './ReviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = [
  'from-blue-500 to-blue-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-purple-500 to-fuchsia-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-600',
];

const computeInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const colorFor = (name = '') => {
  const sum = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[sum % PALETTE.length];
};

// Seed testimonials (shown if no approved DB reviews yet, and merged with DB ones otherwise)
const SEED = [
  {
    id: 'seed-1',
    name: 'Patrick Mukendi',
    trip: { fr: 'Voyage à Dubaï', en: 'Trip to Dubai' },
    quote: {
      fr: "Service impeccable du début à la fin. L'équipe d'IZI Travels a géré tous les détails de mon voyage d'affaires à Dubaï. Aucun stress, que du plaisir !",
      en: "Impeccable service from start to finish. The IZI Travels team handled every detail of my business trip to Dubai. No stress, just pleasure!"
    },
    rating: 5,
  },
  {
    id: 'seed-2',
    name: 'Marie-Claire Bibombe',
    trip: { fr: 'Vacances en France', en: 'Vacation in France' },
    quote: {
      fr: "J'ai voyagé avec mes deux enfants pour la première fois et tout s'est passé à merveille. Conseils précieux, prix compétitif et suivi exemplaire. Je recommande !",
      en: "I traveled with my two children for the first time and everything went wonderfully. Valuable advice, competitive prices and exemplary follow-up. I recommend!"
    },
    rating: 5,
  },
  {
    id: 'seed-3',
    name: 'Jean-Claude Tshimanga',
    trip: { fr: 'Voyage Multi-destinations', en: 'Multi-city Trip' },
    quote: {
      fr: "Un itinéraire complexe Kinshasa - Bruxelles - Istanbul - Casablanca organisé en moins de 24h. Professionnalisme remarquable. IZI Travels est devenu mon agence de référence.",
      en: "A complex Kinshasa - Brussels - Istanbul - Casablanca itinerary organized in less than 24h. Remarkable professionalism. IZI Travels has become my go-to agency."
    },
    rating: 5,
  },
  {
    id: 'seed-4',
    name: 'Esther Kabamba',
    trip: { fr: 'Pèlerinage à La Mecque', en: 'Pilgrimage to Mecca' },
    quote: {
      fr: "Voyage très bien préparé pour mon pèlerinage. L'équipe a anticipé tous mes besoins et le contact était permanent. Que Dieu bénisse IZI Travels.",
      en: "Very well prepared trip for my pilgrimage. The team anticipated all my needs and contact was permanent. May God bless IZI Travels."
    },
    rating: 5,
  },
  {
    id: 'seed-5',
    name: 'Daniel Kalombo',
    trip: { fr: 'Vol Kinshasa-Johannesburg', en: 'Kinshasa-Johannesburg flight' },
    quote: {
      fr: "Excellent rapport qualité-prix. J'ai trouvé un billet à un tarif que je n'avais vu nulle part ailleurs. Réservation rapide et réponse en moins d'une heure.",
      en: "Excellent value for money. I found a ticket at a price I hadn't seen anywhere else. Quick booking and reply in less than an hour."
    },
    rating: 5,
  },
  {
    id: 'seed-6',
    name: 'Grace Mwamba',
    trip: { fr: 'Lune de miel à Zanzibar', en: 'Honeymoon in Zanzibar' },
    quote: {
      fr: "IZI Travels a organisé notre lune de miel à Zanzibar dans les moindres détails. Mon mari et moi en gardons un souvenir inoubliable. Merci à toute l'équipe !",
      en: "IZI Travels organized our Zanzibar honeymoon down to the smallest detail. My husband and I have unforgettable memories. Thanks to the whole team!"
    },
    rating: 5,
  },
];

const Testimonials = () => {
  const { language, t } = useLanguage();
  const [dbReviews, setDbReviews] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const trackRef = useRef(null);
  const autoplayRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews?limit=20`).then((r) => {
      setDbReviews(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setDbReviews([]));
  }, []);

  // Merge DB reviews (have priority) + seed
  const items = useMemo(() => {
    const dbMapped = dbReviews.map((r) => ({
      id: r.id,
      name: r.name,
      trip: { fr: r.trip, en: r.trip },
      quote: { fr: r.message, en: r.message },
      rating: r.rating,
      initials: r.initials || computeInitials(r.name),
      color: r.color || colorFor(r.name),
    }));
    const seedMapped = SEED.map((s) => ({
      ...s,
      initials: computeInitials(s.name),
      color: colorFor(s.name),
    }));
    return [...dbMapped, ...seedMapped];
  }, [dbReviews]);

  // Pagination: 3 per page on lg, handled responsively via CSS but pages logic groups by 3
  const perPage = 3;
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));

  // Auto-rotate
  useEffect(() => {
    if (totalPages <= 1) return;
    autoplayRef.current = setInterval(() => {
      setPage((p) => (p + 1) % totalPages);
    }, 6000);
    return () => clearInterval(autoplayRef.current);
  }, [totalPages]);

  const goPrev = () => setPage((p) => (p - 1 + totalPages) % totalPages);
  const goNext = () => setPage((p) => (p + 1) % totalPages);

  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < items.length; i += perPage) {
      out.push(items.slice(i, i + perPage));
    }
    return out;
  }, [items]);

  return (
    <section id="temoignages" className="py-24 bg-gradient-to-b from-blue-50/40 to-white relative overflow-hidden">
      <div className="absolute top-20 -left-20 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 -right-20 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
            <MessageCircle className="text-blue-600" size={18} />
            <span className="text-sm font-semibold text-blue-700">{t('testimonialsBadge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('testimonialsTitle')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('testimonialsSubtitle')}</p>

          <div className="mt-8 inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-md border border-gray-100">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-gray-900 font-bold text-lg">4.9/5</span>
            <span className="text-gray-500 text-sm">
              {language === 'fr' ? `sur ${Math.max(200, items.length * 30)}+ avis` : `on ${Math.max(200, items.length * 30)}+ reviews`}
            </span>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden" ref={trackRef}>
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${page * 100}%)` }}
            >
              {pages.map((group, gIdx) => (
                <div key={gIdx} className="min-w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-1">
                  {group.map((tm, idx) => (
                    <div
                      key={tm.id || idx}
                      className="group bg-white rounded-2xl p-7 shadow-md hover:shadow-2xl border border-gray-100 hover:border-blue-200 transition-all duration-500 hover:-translate-y-1 relative"
                      data-testid={`testimonial-card-${gIdx}-${idx}`}
                    >
                      <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Quote className="text-white" size={20} />
                      </div>
                      <div className="flex gap-1 mb-4">
                        {[...Array(tm.rating)].map((_, i) => (
                          <Star key={i} size={18} className="fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <p className="text-gray-700 leading-relaxed mb-6 italic min-h-[120px]">
                        "{tm.quote[language] || tm.quote.fr}"
                      </p>
                      <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-5" />
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${tm.color} flex items-center justify-center text-white font-bold shadow-md`}>
                          {tm.initials}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{tm.name}</div>
                          <div className="text-sm text-blue-600">{tm.trip[language] || tm.trip.fr}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Arrows */}
          {totalPages > 1 && (
            <>
              <button
                onClick={goPrev}
                className="hidden lg:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl border border-gray-100 items-center justify-center text-blue-600 hover:bg-blue-50 transition-all hover:scale-110"
                aria-label="Précédent"
                data-testid="testimonials-prev-btn"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goNext}
                className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl border border-gray-100 items-center justify-center text-blue-600 hover:bg-blue-50 transition-all hover:scale-110"
                aria-label="Suivant"
                data-testid="testimonials-next-btn"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        {/* Indicators */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === page ? 'w-10 bg-blue-600' : 'w-2 bg-blue-200 hover:bg-blue-300'
                }`}
                aria-label={`Page ${i + 1}`}
                data-testid={`testimonials-indicator-${i}`}
              />
            ))}
          </div>
        )}

        {/* CTA — Leave a review */}
        <div className="mt-12 text-center">
          <button
            onClick={() => setModalOpen(true)}
            className="group inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-7 py-4 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            data-testid="leave-review-btn"
          >
            <PenSquare size={20} />
            {t('leaveReviewCTA')}
          </button>
          <p className="text-sm text-gray-500 mt-3">{t('leaveReviewHelp')}</p>
        </div>
      </div>

      <ReviewModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
};

export default Testimonials;
